import type { CharacterLink, CharacterRole } from "@schema/catalog.ts";
import { useState } from "react";
import { useCatalog } from "@/lib/catalog/context.tsx";
import styles from "./CharacterAvatars.module.css";

const ROLE_LABEL: Record<CharacterRole, string> = {
  debut: "debut",
  lead: "lead",
  supporting: "supporting",
  cameo: "cameo",
  origin: "origin story",
  "arc-defining": "arc-defining",
};

const MAX_SHOWN = 5;

const initials = (name: string) =>
  name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) =>
      part
        .replace(/[^A-Za-z']/g, "")
        .charAt(0)
        .toUpperCase(),
    )
    .join("");

/** Initial-circles for linked characters; Doomsday-roster members get the accent ring. */
export function CharacterAvatars({ links }: { links: CharacterLink[] }) {
  const catalog = useCatalog();
  // Which circle is pinned open. Hover and focus reveal the name on their own;
  // this exists for touch, where there is no hover at all and the initials
  // would otherwise be unreadable with no way to ask what they mean.
  const [pinned, setPinned] = useState<string | null>(null);
  if (links.length === 0) return null;
  const sorted = [...links].sort((a, b) => b.weight - a.weight);
  const shown = sorted.slice(0, MAX_SHOWN);
  const overflow = sorted.length - shown.length;
  const hiddenNames = sorted
    .slice(MAX_SHOWN)
    .map((l) => catalog.characters.find((c) => c.id === l.id)?.name)
    .filter(Boolean)
    .join(", ");

  return (
    <ul className={styles.avatars} aria-label="Characters">
      {shown.map((link) => {
        const character = catalog.characters.find((c) => c.id === link.id);
        if (!character) return null;
        const onRoster = character.targetRoster;
        const open = pinned === link.id;
        return (
          <li key={link.id}>
            {/*
             * A button rather than a bare <li tabIndex>: this is a disclosure,
             * and making a non-interactive element focusable is the anti-pattern
             * biome's noNoninteractiveTabindex exists to catch. The native
             * `title` is gone — it takes about a second to appear, which is why
             * the initials read as a puzzle, and two tooltips on one circle is
             * worse than none.
             */}
            <button
              type="button"
              className={`${styles.avatar} ${onRoster ? styles.roster : ""} ${open ? styles.open : ""}`}
              aria-expanded={open}
              onClick={() => setPinned(open ? null : link.id)}
            >
              <span aria-hidden="true">{initials(character.name)}</span>
              <span className={styles.srOnly}>
                {character.name}: {ROLE_LABEL[link.role]}
                {onRoster
                  ? `, on the ${catalog.target.shortTitle} ${catalog.project.vocabulary.roster}`
                  : ""}
              </span>
              <span className={styles.label} aria-hidden="true">
                {character.name}
                <em className={styles.role}>
                  {ROLE_LABEL[link.role]}
                  {onRoster
                    ? ` · on the ${catalog.target.shortTitle} ${catalog.project.vocabulary.roster}`
                    : ""}
                </em>
              </span>
            </button>
          </li>
        );
      })}
      {overflow > 0 && (
        <li>
          {/* The hidden ones are named too — an unexplained "+3" is the same
           * puzzle as an unexplained "TR". */}
          <button
            type="button"
            className={`${styles.avatar} ${styles.more} ${pinned === "overflow" ? styles.open : ""}`}
            aria-expanded={pinned === "overflow"}
            onClick={() => setPinned(pinned === "overflow" ? null : "overflow")}
          >
            <span aria-hidden="true">+{overflow}</span>
            <span className={styles.srOnly}>{hiddenNames}</span>
            <span className={styles.label} aria-hidden="true">
              {hiddenNames}
            </span>
          </button>
        </li>
      )}
    </ul>
  );
}
